import { cx } from "../../utils/cx";
import { getStatusConfig } from "../../config/statusConfig";

/**
 * Pill tabs on a grey track. tabs: [{ value, label, icon?, disabled? }]
 */
export function Tabs({ tabs, value, onChange, className }) {
  return (
    <div className={cx("inline-flex items-center gap-1 rounded-lg bg-gray-100 p-1", className)} role="tablist">
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={tab.disabled}
            onClick={() => onChange(tab.value)}
            className={cx(
              "flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-all duration-200",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/20 disabled:cursor-not-allowed disabled:opacity-50",
              active ? "bg-white font-semibold text-brand shadow-sm" : "text-gray-600 hover:bg-white/50 hover:text-gray-900"
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Underlined status tabs with a coloured dot and a count, as on the HEC MIS application lists.
 * tabs: [{ value, label?, count?, color? }]; label and colour default to the status config.
 */
export function StatusTabs({ tabs, value, onChange, className }) {
  return (
    <div className={cx("thin-scrollbar flex overflow-x-auto border-b border-slate-100", className)} role="tablist">
      {tabs.map((tab) => {
        const active = tab.value === value;
        const config = getStatusConfig(tab.value);
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={cx(
              "-mb-px flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-6 py-2 text-sm transition-colors",
              active ? "border-b-brand bg-brand/5 text-brand" : "border-b-transparent text-slate-600 hover:text-slate-900"
            )}
          >
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: tab.color ?? config.color }} />
            {tab.label ?? config.label}
            {tab.count !== undefined && (
              <span className={cx("rounded-full px-2 text-xs leading-5", active ? "bg-brand text-white" : "bg-gray-200 text-gray-700")}>
                {Number(tab.count).toLocaleString("en-US")}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
